import { Settings } from '@streamr/tv-engine/~/settings';
import { EventEmitter } from 'eventemitter3';
import { Timekeeper } from './Timekeeper';
import { Track } from './Track';
/**
 * Extracts audio frames from a MediaStreamTrack using MediaStreamTrackProcessor.
 * This is Chrome-only as MediaStreamTrackProcessor for audio is not supported in other browsers.
 */
export class ChromeAudioFrameExtractor {
    LOG_PREFIX = '[ChromeAudioFrameExtractor]';
    emitter = new EventEmitter();
    track;
    consumer;
    trackProcessor;
    abortController = new AbortController();
    stopped = false;
    outputTrack;
    // Metadata
    frameNumber = 0n;
    cumulativeTimestampMicros = 0n; // Tracks exact sample-based duration (synthetic timestamps)
    trackId;
    timestampOffsetMicros;
    // The downstream encoder (OpusAudioEncoder) is configured for
    // `Settings.audioNumberOfChannels` (1, mono) and expects exactly
    // `Settings.audioFrameSize` samples per encode call. Chrome's
    // MediaStreamTrackProcessor for audio emits AudioData chunks whose
    // numberOfFrames is implementation-defined (typically ~480 = 10 ms on
    // Chrome). If we forwarded raw chunks the encoder would misinterpret
    // interleaved-stereo data as mono and produce 2x-rate audio. Accumulate
    // into a mono buffer of audioFrameSize samples and emit one frame per
    // fill (20 ms at 48 kHz).
    targetSampleCount = Settings.audioFrameSize;
    accumulator = new Float32Array(Settings.audioFrameSize);
    accumulatorWriteIndex = 0;
    constructor(track) {
        this.track = track;
        this.trackId = track.id;
        this.outputTrack = new Track(track.id, true);
    }
    start = () => {
        // Log track settings to detect sample rate mismatches
        const trackSettings = this.track.getSettings();
        console.log(`${this.LOG_PREFIX} start called`, {
            trackId: this.trackId,
            trackSampleRate: trackSettings.sampleRate,
            trackChannelCount: trackSettings.channelCount,
        });
        this.consumer = new WritableStream({
            write: (audioData) => {
                if (this.outputTrack.hasListener()) {
                    this.ingestAudioData(audioData);
                }
                audioData.close();
            },
        });
        this.trackProcessor = new MediaStreamTrackProcessor({
            track: this.track,
        });
        this.trackProcessor.readable
            .pipeTo(this.consumer, {
            signal: this.abortController.signal,
        })
            .catch((_e) => {
            console.log(`${this.LOG_PREFIX} pipeTo canceled`, {
                trackId: this.trackId,
            });
        })
            .finally(() => {
            this.stopped = true;
            console.log(`${this.LOG_PREFIX} pipeline stopped, emitting done`, {
                trackId: this.trackId,
            });
            this.outputTrack?.emit(undefined, true);
            this.emitter.emit('aborted');
        });
    };
    stop = () => {
        console.log(`${this.LOG_PREFIX} stop called`, {
            trackId: this.trackId,
        });
        if (this.stopped) {
            return Promise.resolve();
        }
        return new Promise((resolve) => {
            this.abortController.abort('Aborted');
            this.emitter.once('aborted', () => {
                resolve();
            });
        });
    };
    getOutputTrack() {
        return this.outputTrack;
    }
    getTrackId() {
        return this.trackId;
    }
    /**
     * Pull samples from an AudioData chunk into the accumulator, downmixing
     * to mono on the fly. Emits a frame each time the accumulator fills to
     * `targetSampleCount`. A single AudioData chunk can contribute to
     * multiple emitted frames or to none, depending on its size relative
     * to the target.
     */
    ingestAudioData(audioData) {
        if (this.timestampOffsetMicros === undefined) {
            this.timestampOffsetMicros = Timekeeper.getInstance('producer:individual').getLastTimestampMicros();
            console.log(`${this.LOG_PREFIX} [FIRST_FRAME] captured timestamp offset`, {
                trackId: this.trackId.slice(0, 8),
                timestampOffsetMs: (Number(this.timestampOffsetMicros) / 1000).toFixed(1),
            });
        }
        const numberOfFrames = audioData.numberOfFrames;
        const numChannels = audioData.numberOfChannels;
        const sampleRate = audioData.sampleRate;
        // Copy each channel into a planar staging buffer, then downmix to
        // mono by averaging. For mono input the average is a no-op.
        const planar = new Float32Array(numberOfFrames * numChannels);
        for (let ch = 0; ch < numChannels; ch++) {
            audioData.copyTo(planar.subarray(ch * numberOfFrames, (ch + 1) * numberOfFrames), { planeIndex: ch });
        }
        const mono = new Float32Array(numberOfFrames);
        if (numChannels === 1) {
            mono.set(planar);
        }
        else {
            const inv = 1 / numChannels;
            for (let i = 0; i < numberOfFrames; i++) {
                let sum = 0;
                for (let ch = 0; ch < numChannels; ch++) {
                    sum += planar[ch * numberOfFrames + i];
                }
                mono[i] = sum * inv;
            }
        }
        let readIndex = 0;
        while (readIndex < numberOfFrames) {
            const spaceRemaining = this.targetSampleCount - this.accumulatorWriteIndex;
            const toCopy = Math.min(spaceRemaining, numberOfFrames - readIndex);
            this.accumulator.set(mono.subarray(readIndex, readIndex + toCopy), this.accumulatorWriteIndex);
            this.accumulatorWriteIndex += toCopy;
            readIndex += toCopy;
            if (this.accumulatorWriteIndex === this.targetSampleCount) {
                this.emitAccumulatedFrame(sampleRate);
                this.accumulatorWriteIndex = 0;
            }
        }
    }
    emitAccumulatedFrame(sampleRate) {
        if (this.timestampOffsetMicros === undefined) {
            return;
        }
        const durationMicros = BigInt(Math.round((this.targetSampleCount / sampleRate) * 1_000_000));
        const timestampMicros = this.cumulativeTimestampMicros + this.timestampOffsetMicros;
        this.cumulativeTimestampMicros += durationMicros;
        // Hand ownership of a fresh, exactly-sized buffer to the listener.
        // The accumulator is reused, so we must copy.
        const frameSamples = new Float32Array(this.targetSampleCount);
        frameSamples.set(this.accumulator);
        const unencodedAudioFrame = {
            frameNumber: this.frameNumber++,
            timestamp: timestampMicros,
            duration: durationMicros,
            trackId: this.trackId,
            data: frameSamples,
            done: false,
            isKeyFrame: true,
            producerWallMillis: Date.now(),
        };
        this.outputTrack?.emit(unencodedAudioFrame, false, [
            unencodedAudioFrame.data.buffer,
        ]);
    }
}
